import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import AuthCard from '@/components/auth/AuthCard';
import FormMessage from '@/components/auth/FormMessage';
import PasswordField from '@/components/auth/PasswordField';
import { userManagementService } from '@/api/services/UserManagementService';
import { getStatus, isNetworkError, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { useCooldown } from '@/hooks/useCooldown';
import { focusField, focusFirstError } from '@/lib/focusField';
import { EMAIL_PATTERN, isBlank } from '@/lib/validators';
import { ROUTES } from '@/routes/paths';

interface LoginValues {
  email: string;
  password: string;
}

// Ошибка отправки. Показывается у кнопки: ошибку поля сняла бы проверка при уходе с поля.
type SubmitError = 'credentials' | 'unconfirmed' | 'tooManyAttempts' | 'unavailable' | 'generic';

const ERROR_TEXT: Record<SubmitError, string> = {
  credentials: 'auth.login.errorCredentials',
  unconfirmed: 'auth.login.errorUnconfirmed',
  tooManyAttempts: 'auth.login.errorTooManyAttempts',
  unavailable: 'auth.errors.unavailable',
  generic: 'auth.errors.generic',
};

function submitErrorOf(error: unknown): SubmitError {
  const status = getStatus(error);
  // 204 «нет пользователя» и 401 «неверный пароль» намеренно не различаем: иначе форма раскрывает, зарегистрирован ли email.
  if (status === 204 || status === 401) {
    return 'credentials';
  }
  if (status === 406) {
    return 'unconfirmed';
  }
  if (status === 429) {
    return 'tooManyAttempts';
  }
  return isNetworkError(error) || isServiceUnavailable(error) ? 'unavailable' : 'generic';
}

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const emailRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const resend = useCooldown(30);
  // Сервер не сообщает срок блокировки (Retry-After), поэтому пауза лишь не даёт сразу слать новые попытки.
  const attemptPause = useCooldown(30);
  const formRef = useRef<HTMLFormElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => emailRef.current?.focus(), []);

  const form = useForm<LoginValues>({
    mode: 'uncontrolled',
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) =>
        isBlank(value)
          ? t('auth.validation.required')
          : EMAIL_PATTERN.test(value.trim())
            ? null
            : t('auth.validation.email'),
      password: (value) => (isBlank(value) ? t('auth.validation.required') : null),
    },
    validateInputOnBlur: true,
  });

  // Кнопка с индикатором загрузки теряет фокус, поэтому после ошибки возвращаем его туда, где нужно действие.
  useEffect(() => {
    if (submitError === 'credentials') {
      focusField(form.getInputNode('password'));
    } else if (submitError) {
      alertRef.current?.focus();
    }
    // form стабилен, реагируем только на новую ошибку.
  }, [submitError]);

  function handleInvalid(errors: Record<string, unknown>): void {
    setSubmitError(null);
    focusFirstError(formRef.current, errors);
  }

  async function handleSubmit(values: LoginValues): Promise<void> {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await login({ userName: values.email.trim(), password: values.password });
      navigate(returnTo ?? ROUTES.APP, { replace: true });
    } catch (error) {
      const kind = submitErrorOf(error);
      setSubmitError(kind);
      if (kind === 'tooManyAttempts') {
        attemptPause.start();
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    const email = form.getValues().email.trim();
    if (!email) {
      return;
    }
    try {
      await userManagementService.resendRegistrationEmail(email);
      resend.start();
      notifications.show({ color: 'lime', message: t('auth.login.resendSent') });
    } catch {
      notifications.show({ color: 'red', message: t('auth.errors.generic') });
    }
  }

  return (
    <AuthCard
      title={t('auth.login.title')}
      footer={
        <>
          {/* При неверных данных ссылка на восстановление уже есть в сообщении об ошибке, вторая рядом не нужна. */}
          {submitError === 'credentials' ? null : (
            <Anchor component={Link} to={ROUTES.FORGOT_PASSWORD} size="sm" c="brand.7" underline="always">
              {t('auth.login.forgot')}
            </Anchor>
          )}
          <Text size="sm">
            {t('auth.login.noAccount')}{' '}
            <Anchor component={Link} to={ROUTES.REGISTER} c="brand.7" underline="always">
              {t('auth.login.registerLink')}
            </Anchor>
          </Text>
        </>
      }
    >
      <form ref={formRef} onSubmit={form.onSubmit(handleSubmit, handleInvalid)} noValidate>
        <Stack gap="md">
          <TextInput
            ref={emailRef}
            label={t('auth.fields.email')}
            type="email"
            autoComplete="username"
            required
            key={form.key('email')}
            {...form.getInputProps('email')}
          />

          <PasswordField
            label={t('auth.fields.password')}
            autoComplete="current-password"
            required
            key={form.key('password')}
            {...form.getInputProps('password')}
          />

          {submitError ? (
            <FormMessage ref={alertRef}>
              <span>{t(ERROR_TEXT[submitError])}</span>
              {submitError === 'credentials' ? (
                <Anchor component={Link} to={ROUTES.FORGOT_PASSWORD} size="sm" c="brand.7" underline="always">
                  {t('auth.login.recover')}
                </Anchor>
              ) : null}
              {submitError === 'unconfirmed' ? (
                <Button variant="default" size="compact-sm" disabled={resend.active} onClick={handleResend}>
                  {resend.active
                    ? t('auth.login.resendRetry', { seconds: resend.remaining })
                    : t('auth.login.resendConfirm')}
                </Button>
              ) : null}
            </FormMessage>
          ) : null}

          <Button type="submit" loading={submitting} disabled={attemptPause.active} fullWidth mt="xs">
            {attemptPause.active
              ? t('auth.login.retryIn', { seconds: attemptPause.remaining })
              : t('auth.login.submit')}
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
